import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import { Panel, Row, Table } from "react-bootstrap";
import {
  isSameDay,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  isWeekend
} from "date-fns";

import { DailyCallCompletionFilters } from "./components";

import { getWeekPeriod } from "./utils";

import Loading from "../../common/Loading";

import { generateUniqueId } from "../../../utils";

import * as reportActions from "../../../actions/reportActions";
import * as exportActions from "../../../actions/exportActions";
import * as codeActions from "../../../actions/codeActions";

import { formatDate } from "../../../helpers/date";

const DailyCallCompletion = ({
  actions,
  isMobile,
  report,
  loading,
  exporting,
  exported,
  codes
}) => {
  const [filters, setFilters] = useState({
    period: getWeekPeriod(new Date()),
    department: null
  });
  const [data, setData] = useState([]);

  useEffect(() => {
    actions.loadCodes();
  }, [actions]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Daily-Call-Completion-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  useEffect(() => {
    if (report.length && codes.length) {
      const startToDate = startOfDay(new Date(filters.period.startDate));
      const endToDate = endOfDay(new Date(filters.period.endDate));

      const reservedStatus = codes.find(
        (code) =>
          code.entity === "Call" &&
          code.field === "status" &&
          code.label === "Reserved"
      );

      const newResult = report.map((dep) => ({
        ...dep,
        users: dep.users
          .map((user) => {
            const beforeDateRange = [];
            const withinDateRange = [];
            user.dates.forEach((date) => {
              const toDate = new Date(date.date);
              if (toDate < startToDate) {
                beforeDateRange.push({
                  ...date,
                  calls: date.calls.filter(
                    (call) => String(call.stage) === String(reservedStatus._id)
                  )
                });
              } else if (toDate >= startToDate && toDate <= endToDate) {
                withinDateRange.push({
                  ...date,
                  span: Boolean(date.total < 8),
                  calls: date.calls.filter(
                    (call) => String(call.stage) === String(reservedStatus._id)
                  )
                });
              }
            });
            return {
              user: user.user,
              beforeDateRange: beforeDateRange.sort(
                (a, b) => new Date(a.date) - new Date(b.date)
              ),
              withinDateRange: eachDayOfInterval({
                start: startToDate,
                end: endToDate
              })
                .filter((date) => !isWeekend(date))
                .map((date) => {
                  const items = withinDateRange.filter((_date) =>
                    isSameDay(new Date(_date.date), new Date(date))
                  );
                  const item = items.length
                    ? {
                        date,
                        total: items.reduce((a, b) => a + b.total, 0),
                        span: items.some((a) => a.span),
                        calls: items.flatMap((a) => a.calls)
                      }
                    : {
                        date,
                        total: 0,
                        span: true,
                        calls: []
                      };
                  return item;
                })
                .sort((a, b) => new Date(a.date) - new Date(b.date))
            };
          })
          .filter(
            (user) =>
              user.beforeDateRange.length ||
              user.withinDateRange.some(
                (date) => date.span || date.calls.length
              )
          )
      }));
      setData(newResult);
    }
  }, [codes, filters, report]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExportPDF = () => {
    const payload = {
      data: {
        department: filters.department,
        startDate: startOfDay(new Date(filters.period.startDate)),
        endDate: endOfDay(new Date(filters.period.endDate))
      },
      type: "DAILY_CALL_COMPLETION",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div>
      <DailyCallCompletionFilters
        onFiltersChange={handleFiltersChange}
        isMobile={isMobile}
        onFetchData={() =>
          actions.getDailyCallCompletionReport({
            department: filters.department,
            startDate: startOfDay(new Date(filters.period.startDate)),
            endDate: endOfDay(new Date(filters.period.endDate))
          })
        }
        onPDFExport={handleExportPDF}
        exportDisabled={loading || exporting || !report?.length}
      />
      {loading && (
        <Row style={{ display: "flex", justifyContent: "center" }}>
          <Loading />
        </Row>
      )}
      {!loading &&
        data.map((item) => (
          <Panel
            style={{ marginLeft: 15, marginRight: 15 }}
            key={item.department}
            id={`panel-for-${item.department}`}
            defaultExpanded
          >
            <Panel.Heading>
              <Panel.Title toggle>{item.department}</Panel.Title>
            </Panel.Heading>
            <Panel.Collapse>
              <Panel.Body>
                {item.users.map((_item) => (
                  <Panel
                    key={_item.user}
                    id={`panel-for-${_item.user}`}
                    defaultExpanded
                  >
                    <Panel.Heading>
                      <Panel.Title toggle>{_item.user}</Panel.Title>
                    </Panel.Heading>
                    <Panel.Collapse>
                      <Panel.Body>
                        <Table responsive>
                          <thead>
                            <tr>
                              <th>Call Date</th>
                              <th>Customer Name</th>
                              <th>Project Name</th>
                              <th>Actual Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {_item.beforeDateRange.map((date) => (
                              <React.Fragment key={generateUniqueId()}>
                                {date.calls.map((call) => (
                                  <tr key={call._id}>
                                    <td>
                                      {formatDate(
                                        new Date(date.date),
                                        "MM/dd/yyyy"
                                      )}
                                    </td>
                                    <td>{call.customerDetails.name}</td>
                                    <td>{call.projectDetails.name}</td>
                                    <td>{call.actual_hrs}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                            {_item.withinDateRange.map((date) => (
                              <React.Fragment key={generateUniqueId()}>
                                {date.span ? (
                                  <tr key={date.date}>
                                    <td colSpan="100%">{`${formatDate(
                                      new Date(date.date),
                                      "MM/dd/yyyy"
                                    )} Total Actual Hours: ${date.total}`}</td>
                                  </tr>
                                ) : null}
                                {date.calls.map((call) => (
                                  <tr key={call._id}>
                                    <td>
                                      {formatDate(
                                        new Date(date.date),
                                        "MM/dd/yyyy"
                                      )}
                                    </td>
                                    <td>{call.customerDetails.name}</td>
                                    <td>{call.projectDetails.name}</td>
                                    <td>{call.actual_hrs}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </Table>
                      </Panel.Body>
                    </Panel.Collapse>
                  </Panel>
                ))}
              </Panel.Body>
            </Panel.Collapse>
          </Panel>
        ))}
    </div>
  );
};

DailyCallCompletion.propTypes = {
  actions: PropTypes.object,
  isMobile: PropTypes.bool,
  report: PropTypes.array,
  loading: PropTypes.bool,
  exported: PropTypes.bool,
  exporting: PropTypes.bool,
  codes: PropTypes.array
};

function mapStatesToProps(state) {
  return {
    report: state.reducers.DCCReport,
    loading: state.reducers.loading,
    codes: state.reducers.codes,
    exported: state.reducers.exported,
    exporting: state.reducers.exporting
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...reportActions,
        ...exportActions,
        ...codeActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(DailyCallCompletion);
